import React, { useCallback, useMemo } from 'react';
import { Alert } from 'antd';
import { Link, useSearchParams } from 'react-router-dom';

import TagManager from '../tags/TagManager';
import TagBadge from '../tags/TagBadge';
import ItemTagCardRows from './ItemTagCardRows';
import GroupHeader from '../common/GroupHeader';
import { buildMemberCardOptions } from '../cards/CardVisuals';
import useCollectionTags, { invalidateTagOwner } from '../../hooks/useCollectionTags';
import useEnsureCards from '../../hooks/useEnsureCards';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { getCollectionFamilyCardIds } from '../../utils/collections';
import { checkTagUpdate, createTag, deleteTag, getCollectionTags, mergeTag, restoreTag, updateTag } from '../../services/tags';
import { ancestorsOf, managesParentOf } from '../../utils/collectionFamily';
import { normalizeId } from '../../utils/ids';
import { canManage as managesCollection } from '../../utils/permissions';
import { groupByOwner, tagIdOf, tagOwnerCollectionId, tagPagePath } from '../../utils/tags';
import { isAutomaticTag } from '../../utils/tagRules';

/**
 * Settings › Tags (managers; the tab itself is gated by CollectionForm —
 * docs/plans/tags.md §5.2): the collection's own tags, managed with
 * components/tags/TagManager — the tree, Add tag, Edit…, Restricted, Move to…,
 * Delete… and the deleted ones to restore. Like Sharing and Automations,
 * everything here applies IMMEDIATELY through its own endpoints, outside the
 * settings page's Save button.
 *
 * A SUB-COLLECTION's items may carry its parent's tags, so the parent's are
 * listed below its own, read-only, with the door to where they are managed
 * (the InheritedRules grammar). The vocabulary comes from one request
 * (hooks/useCollectionTags), and a sub-collection-only reader never gets the
 * parent's restricted tags: the API leaves them out (ruled d24).
 *
 * Every write publishes the collection's new list to every mounted reader of
 * it, and drops the cached vocabularies of its sub-collections, which carry
 * its tags.
 *
 * ITEM TAGS (kind 'item') appear as one row per card that uses its items as
 * tags — a count and a search, never the full list (plan §5.2; T4d,
 * ItemTagCardRows) — from the same vocabulary answer (`itemTagCards`). A
 * found item tag of this collection's items has **Move to…** (b58): under
 * one of the tags here, or back to the top level.
 *
 * AUTOMATIC TAGS (T6a): a collection tag's ⋯ has **Apply by a rule…**, the
 * rule's editor; `?rule=<tagId>` (the "Why" pop-over's Edit rule) opens it.
 *
 * APPLY TO NEW ITEMS OF (T7, ruled d22): a restricted tag's Edit… offers the
 * collection's cards and its sub-collections' own (the family's cards, in
 * hierarchy order) — the cards the API accepts.
 */
export default function TagsTab({ collectionId, collection }) {
	const id = normalizeId(collectionId);
	const vocabulary = useCollectionTags(id);
	const { tags: all, itemTagCards, publish, loading, error, refresh } = vocabulary;
	const own = useMemo(() => all.filter(tag => tagOwnerCollectionId(tag) === id), [all, id]);
	const inherited = useMemo(() => groupByOwner(all.filter(tag => tagOwnerCollectionId(tag) && tagOwnerCollectionId(tag) !== id)), [all, id]);
	const archived = collection?.status === 'archived';
	const [searchParams] = useSearchParams();
	const rules = useMemo(() => ({ collection }), [collection]);
	const cardDefinitions = useOptionalCardDefinitions();
	const cardsById = cardDefinitions?.cardsById;
	const familyCardIds = useMemo(
		() => [...getCollectionFamilyCardIds(collection, Object.values(cardsById || {}))],
		[cardsById, collection]
	);
	// A sub-collection's own card this session has not seen yet is fetched.
	useEnsureCards(familyCardIds);
	const newItemCards = useMemo(() => {
		const defs = Object.values(cardsById || {});
		const options = buildMemberCardOptions(defs, new Set(familyCardIds));
		const covered = new Set(options.map(option => option.value));
		const refName = id => (Array.isArray(collection?.cards) ? collection.cards : [])
			.find(ref => normalizeId(ref?.cardDefinitionId) === id)?.name || 'Untitled card';
		return [
			...options,
			...familyCardIds.filter(id => !covered.has(id)).map(id => ({ value: id, card: { name: refName(id) }, depth: 0, searchText: refName(id) })),
		];
	}, [cardsById, familyCardIds, collection]);

	const service = useMemo(() => ({
		create: fields => createTag({ collectionId: id }, fields),
		update: (tagId, patch) => updateTag(tagId, patch),
		// b75: the dry run the tag dialog asks before "Apply to new items of" widens.
		check: (tagId, patch) => checkTagUpdate(tagId, patch),
		remove: (tagId, options) => deleteTag(tagId, options),
		restore: tagId => restoreTag(tagId),
		listDeleted: () => getCollectionTags(id, { status: 'deleted' }),
		merge: (tagId, into, options) => mergeTag(tagId, into, options),
	}), [id]);

	// The caller's list is the whole vocabulary: the manager rewrites its own
	// tags and the parent's ride along untouched.
	const onReplace = useCallback(list => {
		const others = all.filter(tag => tagOwnerCollectionId(tag) !== id);
		publish([...list, ...others]);
	}, [all, id, publish]);
	const onChanged = useCallback(() => invalidateTagOwner(id, { except: id }), [id]);

	const ancestors = ancestorsOf(collection);
	const nameOf = ownerId => ancestors.find(a => normalizeId(a._id) === ownerId)?.name || 'the parent collection';

	return (
		<div className="settings-column tags-tab">
			<Alert
				type="info"
				showIcon
				message="Changes on this tab apply immediately"
				description="These are the collection’s tags: everyone who can see the collection sees them. A restricted tag is put on items and taken off by managers only."
			/>
			<section className="form-panel">
				<div className="micro-label form-panel__kicker">Collection tags</div>
				<TagManager
					tags={own}
					loading={loading}
					error={error}
					onRetry={refresh}
					canManage
					readOnlyNote={archived ? 'This collection is archived, so its tags cannot change. Unarchive it to change them.' : null}
					allowRestricted
					service={service}
					onReplace={onReplace}
					onChanged={onChanged}
					rules={rules}
					newItemCards={newItemCards}
					openRuleTagId={normalizeId(searchParams.get('rule')) || null}
					label="Tags"
					emptyText="This collection has no tags yet. Add one, and anyone who can edit its items can put it on them."
				/>
			</section>
			{/* Move to… on an item tag (b58): managers, the collection not archived. */}
			<ItemTagCardRows
				collectionId={id}
				rows={itemTagCards}
				loading={loading}
				error={error}
				onRetry={refresh}
				ancestors={ancestorsOf(collection)}
				tags={own}
				canManage={managesCollection(collection) && !archived}
			/>
			{inherited.map(group => {
				const ownerId = group.collectionId;
				const ownerName = nameOf(ownerId);
				return (
					<section key={group.key} className="form-panel tags-tab__inherited" data-testid="inherited-tags">
						<GroupHeader as="h4" label={`Inherited from ${ownerName}`} count={group.tags.length} />
						<ul className="tag-tree" aria-label={`Tags inherited from ${ownerName}`}>
							{group.tags.map(tag => (
								<li key={tagIdOf(tag)}>
									<div className="tag-tree__row" style={{ '--tag-depth': tag.depth || 1 }}>
										<div className="tag-tree__main">
											<TagBadge tag={tag} size="sm" to={tagPagePath(tag) || undefined} automatic={isAutomaticTag(tag)} />
											{tag.description ? <span className="tag-tree__description" title={tag.description}>{tag.description}</span> : null}
										</div>
									</div>
								</li>
							))}
						</ul>
						<p className="meta-line tag-manager__note">
							{managesParentOf(collection)
								? <>{`Defined on ${ownerName}, these can go on this collection’s items too. `}<Link to={`/collections/${ownerId}/form?tab=tags`}>{`Change them on ${ownerName}`}</Link></>
								: `Defined on ${ownerName}, these can go on this collection’s items too. Someone who manages ${ownerName} can change them there.`}
						</p>
					</section>
				);
			})}
		</div>
	);
}
